import React from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Edit2, Trash2, GripVertical, Repeat } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getTheme } from '../themes';
import { playCheckSound, playUncheckSound } from '../services/sounds';

// Helper: resolve checkbox completed color by theme and category
const getCheckboxColor = (theme, category) => {
  const colorMap = {
    executive: { A: '#DAA520', B: '#DAA520', C: '#DAA520' },
    inkpaper:  { A: '#1e3a5f', B: '#1e3a5f', C: '#1e3a5f' },
    forest:    { A: '#2d5a3e', B: '#4a7c59', C: '#b8943f' },
    ocean:     { A: '#1a4a6e', B: '#1a6080', C: '#3a8a9a' },
    floral:    { A: '#c45a78', B: '#8a6aaa', C: '#6a9a6a' },
    sunset:    { A: '#c41a0a', B: '#e06010', C: '#c89800' },
  };
  const defaultColors = { A: '#b91c1c', B: '#1d4ed8', C: '#15803d' };
  return (colorMap[theme] || defaultColors)[category] || defaultColors[category];
};

const categoryColors = {
  A: {
    bg: 'bg-red-50 dark:bg-red-950',
    border: 'border-red-600',
    text: 'text-red-800 dark:text-red-200',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    hover: 'hover:bg-red-100 dark:hover:bg-red-900',
    checkboxBg: 'bg-red-700',
    checkboxBorder: 'border-red-700'
  },
  B: {
    bg: 'bg-blue-50 dark:bg-blue-950',
    border: 'border-blue-600',
    text: 'text-blue-800 dark:text-blue-200',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    hover: 'hover:bg-blue-100 dark:hover:bg-blue-900',
    checkboxBg: 'bg-blue-700',
    checkboxBorder: 'border-blue-700'
  },
  C: {
    bg: 'bg-green-50 dark:bg-green-950',
    border: 'border-green-600',
    text: 'text-green-800 dark:text-green-200',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    hover: 'hover:bg-green-100 dark:hover:bg-green-900',
    checkboxBg: 'bg-green-700',
    checkboxBorder: 'border-green-700'
  }
};

const getThemeColors = (category, theme) => {
  if (theme === 'inkpaper') {
    // For Ink & Paper theme - all navy blue, no category colors
    return {
      bg: 'bg-transparent',
      border: 'border-[#1e3a5f]',
      text: 'text-[#1e3a5f] dark:text-[#1e3a5f]',
      descriptionText: 'text-[#1e3a5f] dark:text-[#1e3a5f] font-medium',
      badge: 'bg-[#F5F0E8] text-[#1e3a5f] dark:bg-[#F5F0E8] dark:text-[#1e3a5f]',
      hover: 'hover:bg-[#FFF9E6]',
      checkboxBg: 'bg-[#1e3a5f]',
      checkboxBorder: 'border-[#1e3a5f]'
    };
  }
  
  if (theme === 'executive') {
    return {
      bg: 'bg-transparent',
      border: 'border-[#DAA520]',
      text: 'text-[#0D2847] dark:text-[#1A2A3A]',
      descriptionText: 'text-[#0D2847] dark:text-[#1A2A3A] font-executive-body',
      badge: 'bg-[#B0B0B0] text-[#0D2847] dark:bg-[#888888] dark:text-[#1A2A3A]',
      hover: 'hover:bg-[#C0C0C0] dark:hover:bg-[#989898]',
      checkboxBg: 'bg-[#DAA520]',
      checkboxBorder: 'border-[#DAA520]'
    };
  }

  if (theme === 'frostedglass') {
    // Frosted Glass - same category colors as Prime but with glass styling
    const colors = categoryColors[category];
    return {
      ...colors,
      bg: 'frosted-task-card',
    };
  }

  if (theme === 'forest') {
    const forestColors = {
      A: {
        bg: 'bg-[#c8d9ce] dark:bg-[#1c2e22]',
        border: 'border-[#2d5a3e]',
        text: 'text-[#1a3e28] dark:text-[#a8d4b4]',
        badge: 'bg-[#a8c4ae] text-[#1a3e28] dark:bg-[#2a4a32] dark:text-[#a8d4b4]',
        hover: 'hover:bg-[#b8ccbf] dark:hover:bg-[#243a2a]',
        checkboxBg: 'bg-[#2d5a3e]',
        checkboxBorder: 'border-[#2d5a3e]'
      },
      B: {
        bg: 'bg-[#e4f0e8] dark:bg-[#243a2c]',
        border: 'border-[#4a7c59]',
        text: 'text-[#1e4a2a] dark:text-[#a8d4b8]',
        badge: 'bg-[#b8d4c0] text-[#1e4a2a] dark:bg-[#2a4a32] dark:text-[#a8d4b8]',
        hover: 'hover:bg-[#d4e8da] dark:hover:bg-[#284030]',
        checkboxBg: 'bg-[#4a7c59]',
        checkboxBorder: 'border-[#4a7c59]'
      },
      C: {
        bg: 'bg-[#f5f0e0] dark:bg-[#332d1a]',
        border: 'border-[#b8943f]',
        text: 'text-[#5a4a1a] dark:text-[#e0d4a8]',
        badge: 'bg-[#e8ddb8] text-[#5a4a1a] dark:bg-[#4a4020] dark:text-[#e0d4a8]',
        hover: 'hover:bg-[#ede6d0] dark:hover:bg-[#403820]',
        checkboxBg: 'bg-[#b8943f]',
        checkboxBorder: 'border-[#b8943f]'
      }
    };
    return forestColors[category];
  }

  if (theme === 'ocean') {
    const oceanColors = {
      A: {
        bg: 'bg-[#9cb8d4] dark:bg-[#122a44]',
        border: 'border-[#1a4a6e]',
        text: 'text-[#0d2b4a] dark:text-[#a8c8e8]',
        badge: 'bg-[#a0bcd4] text-[#0d2b4a] dark:bg-[#1a3a5e] dark:text-[#a8c8e8]',
        hover: 'hover:bg-[#8cacc8] dark:hover:bg-[#1a3450]',
        checkboxBg: 'bg-[#1a4a6e]',
        checkboxBorder: 'border-[#1a4a6e]'
      },
      B: {
        bg: 'bg-[#a0c4d8] dark:bg-[#0e3838]',
        border: 'border-[#1a6080]',
        text: 'text-[#0a3a5a] dark:text-[#88d4d4]',
        badge: 'bg-[#88b8cc] text-[#0a3a5a] dark:bg-[#163a3a] dark:text-[#88d4d4]',
        hover: 'hover:bg-[#90b8d0] dark:hover:bg-[#164444]',
        checkboxBg: 'bg-[#1a6080]',
        checkboxBorder: 'border-[#1a6080]'
      },
      C: {
        bg: 'bg-[#a8d4e0] dark:bg-[#143840]',
        border: 'border-[#3a8a9a]',
        text: 'text-[#1a5a68] dark:text-[#a0dce8]',
        badge: 'bg-[#a8dce4] text-[#1a5a68] dark:bg-[#1a3e44] dark:text-[#a0dce8]',
        hover: 'hover:bg-[#98c8d8] dark:hover:bg-[#1a444c]',
        checkboxBg: 'bg-[#3a8a9a]',
        checkboxBorder: 'border-[#3a8a9a]'
      }
    };
    return oceanColors[category];
  }

  if (theme === 'floral') {
    const floralColors = {
      A: {
        bg: 'bg-[#fce0e6] dark:bg-[#3a2028]',
        border: 'border-[#c45a78]',
        text: 'text-[#6a2040] dark:text-[#f0c0d0]',
        badge: 'bg-[#f0b8c8] text-[#6a2040] dark:bg-[#5a2838] dark:text-[#f0c0d0]',
        hover: 'hover:bg-[#f8d0da] dark:hover:bg-[#482830]',
        checkboxBg: 'bg-[#c45a78]',
        checkboxBorder: 'border-[#c45a78]'
      },
      B: {
        bg: 'bg-[#ede0f4] dark:bg-[#2e2238]',
        border: 'border-[#8a6aaa]',
        text: 'text-[#4a2868] dark:text-[#d0b8e8]',
        badge: 'bg-[#d0b8e0] text-[#4a2868] dark:bg-[#3e2850] dark:text-[#d0b8e8]',
        hover: 'hover:bg-[#e0d0ec] dark:hover:bg-[#382a44]',
        checkboxBg: 'bg-[#8a6aaa]',
        checkboxBorder: 'border-[#8a6aaa]'
      },
      C: {
        bg: 'bg-[#e0f0e4] dark:bg-[#1e2e22]',
        border: 'border-[#6a9a6a]',
        text: 'text-[#2a5a30] dark:text-[#a8d4ac]',
        badge: 'bg-[#b8d8bc] text-[#2a5a30] dark:bg-[#284030] dark:text-[#a8d4ac]',
        hover: 'hover:bg-[#d0e8d4] dark:hover:bg-[#263828]',
        checkboxBg: 'bg-[#6a9a6a]',
        checkboxBorder: 'border-[#6a9a6a]'
      }
    };
    return floralColors[category];
  }

  if (theme === 'sunset') {
    const sunsetColors = {
      A: {
        bg: 'bg-[#dc6850] dark:bg-[#4e1a10]',
        border: 'border-[#c41a0a]',
        text: 'text-[#3a0400] dark:text-[#f8c8c0]',
        badge: 'bg-[#c85040] text-[#fff0e8] dark:bg-[#6a2818] dark:text-[#f8c8c0]',
        hover: 'hover:bg-[#d05840] dark:hover:bg-[#5e2014]',
        checkboxBg: 'bg-[#c41a0a]',
        checkboxBorder: 'border-[#c41a0a]'
      },
      B: {
        bg: 'bg-[#f09030] dark:bg-[#5a3010]',
        border: 'border-[#e06010]',
        text: 'text-[#3a1400] dark:text-[#f8d8b0]',
        badge: 'bg-[#e07820] text-[#fff0e0] dark:bg-[#6a4010] dark:text-[#f8d8b0]',
        hover: 'hover:bg-[#e88020] dark:hover:bg-[#683810]',
        checkboxBg: 'bg-[#e06010]',
        checkboxBorder: 'border-[#e06010]'
      },
      C: {
        bg: 'bg-[#e8c020] dark:bg-[#4a3808]',
        border: 'border-[#c89800]',
        text: 'text-[#3a2800] dark:text-[#f0e098]',
        badge: 'bg-[#d4a810] text-[#3a2800] dark:bg-[#584810] dark:text-[#f0e098]',
        hover: 'hover:bg-[#e0b410] dark:hover:bg-[#54400c]',
        checkboxBg: 'bg-[#c89800]',
        checkboxBorder: 'border-[#c89800]'
      }
    };
    return sunsetColors[category];
  }
  
  // For Prime theme - use category colors
  return categoryColors[category];
};

const TaskCard = ({ task, taskNumber, showTaskNumber, onToggleComplete, onEdit, onDelete, onStatusChange, showDescription = true, showDate = false }) => {
  const theme = localStorage.getItem('theme') || 'prime';
  const colors = getThemeColors(task.category, theme);
  const currentTheme = getTheme(theme);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: task.id,
    data: {
      type: 'task',
      task: task
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getStatusColor = (status) => {
    // Executive theme uses navy colors
    if (theme === 'executive') {
      switch (status) {
        case 'complete':
          return 'text-[#0D2847] bg-[#8AB88E] border-[#DAA520]';
        case 'in-process':
          return 'text-[#0D2847] bg-[#8AAED0] border-[#DAA520]';
        case 'waiting':
          return 'text-[#0D2847] bg-[#D4B070] border-[#DAA520]';
        default:
          return 'text-[#0D2847] bg-[#B0B0B0] border-[#DAA520]';
      }
    }
    if (theme === 'forest') {
      switch (status) {
        case 'complete':
          return 'text-[#1e4a2a] bg-[#d4e8da] border-[#4a7c59]';
        case 'in-process':
          return 'text-[#1a3e28] bg-[#c8dccf] border-[#2d5a3e]';
        case 'waiting':
          return 'text-[#5a4a1a] bg-[#f0e8c8] border-[#b8943f]';
        default:
          return 'text-[#2a3a28] bg-[#e0e8dc] border-[#7a8a74]';
      }
    }
    if (theme === 'ocean') {
      switch (status) {
        case 'complete':
          return 'text-[#0a3a5a] bg-[#90b8d0] border-[#1a6080]';
        case 'in-process':
          return 'text-[#0d2b4a] bg-[#8cacc8] border-[#1a4a6e]';
        case 'waiting':
          return 'text-[#1a5a68] bg-[#98c8d8] border-[#3a8a9a]';
        default:
          return 'text-[#0d2b4a] bg-[#a0bcd0] border-[#5a8aaa]';
      }
    }
    if (theme === 'floral') {
      switch (status) {
        case 'complete':
          return 'text-[#2a5a30] bg-[#d0e8d4] border-[#6a9a6a]';
        case 'in-process':
          return 'text-[#6a2040] bg-[#f8d0da] border-[#c45a78]';
        case 'waiting':
          return 'text-[#4a2868] bg-[#e0d0ec] border-[#8a6aaa]';
        default:
          return 'text-[#4a2040] bg-[#f0e0e4] border-[#c8a0b0]';
      }
    }
    if (theme === 'sunset') {
      switch (status) {
        case 'complete':
          return 'text-[#f8e8d8] bg-[#8a6040] border-[#a07050]';
        case 'in-process':
          return 'text-[#4a1a04] bg-[#f8a840] border-[#e07010]';
        case 'waiting':
          return 'text-[#3a2800] bg-[#f0cc40] border-[#c89800]';
        default:
          return 'text-[#4a2010] bg-[#e8c8a0] border-[#c09060]';
      }
    }
    switch (status) {
      case 'complete':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'in-process':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'waiting':
        return 'text-amber-700 bg-amber-50 border-amber-200';
      default:
        return 'text-slate-700 bg-slate-50 border-slate-200';
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`${colors.bg} ${colors.border} border-l-4 shadow-lg hover:shadow-xl transition-all duration-200 
        ${task.completed ? 'opacity-60' : ''} 
        ${isDragging ? 'opacity-50 rotate-2' : ''} 
        ${theme === 'inkpaper' ? 'task-card-inkpaper' : ''}
        hover:-translate-y-1 touch-pan-y`}
    >
      <CardHeader className="py-2 sm:py-2.5">
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            <div {...listeners} {...attributes} className="mt-0.5 cursor-grab active:cursor-grabbing touch-none print-hide">
              <GripVertical className={`h-4 w-4 sm:h-5 sm:w-5 ${colors.text} opacity-60 hover:opacity-100 transition-opacity`} />
            </div>
            {showTaskNumber && (
              <div className="flex-shrink-0 mt-0.5">
                <span 
                  className={`font-bold text-sm sm:text-base ${colors.text} ${task.manual_position ? 'underline' : ''}`}
                  title={task.manual_position ? `Manually set to position ${task.manual_position}` : ''}
                >
                  {task.manual_position || taskNumber}.
                </span>
              </div>
            )}
            <Checkbox
              data-testid={`task-checkbox-${task.id}`}
              checked={task.completed}
              onCheckedChange={() => {
                if (!task.completed) playCheckSound();
                else playUncheckSound();
                onToggleComplete(task.id);
              }}
              className={`mt-0.5 border-2 ${colors.checkboxBorder} flex-shrink-0 shadow-sm`}
              style={task.completed ? {
                backgroundColor: getCheckboxColor(theme, task.category),
                borderColor: getCheckboxColor(theme, task.category),
                color: 'white'
              } : {}}
            />
            <div className="flex-1 min-w-0 overflow-hidden">
              <h3
                className={`font-semibold text-sm sm:text-base ${colors.text} ${task.completed ? 'line-through' : ''} break-words leading-tight flex items-center gap-2 ${theme === 'executive' ? 'font-executive-body' : ''}`}
                style={theme === 'executive' ? { color: '#0D2847' } : {}}
              >
                <span className="break-words overflow-hidden">{task.title}</span>
                {(task.is_recurring || task.isRecurring) && (
                  <Repeat className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" title={`Recurring: ${task.recurrence_frequency || task.recurrenceFrequency || 'daily'}`} />
                )}
              </h3>
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0 print-hide">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(task)}
              className={`h-6 w-6 sm:h-7 sm:w-7 ${colors.hover} ${colors.text} hover:scale-110 transition-transform`}
            >
              <Edit2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(task.id)}
              className={`h-6 w-6 sm:h-7 sm:w-7 ${colors.hover} ${colors.text} hover:scale-110 transition-transform`}
            >
              <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      {((showDescription && task.description) || true) && (
        <CardContent className="pt-0 pb-2 space-y-1.5">
          {showDescription && task.description && (
            <p className={`text-xs sm:text-sm break-words leading-snug ${
              theme === 'inkpaper'
                ? 'text-[#1e3a5f] dark:text-[#1e3a5f] font-medium'
                : theme === 'executive'
                ? 'text-[#0D2847] dark:text-[#0D2847] font-executive-body'
                : 'text-slate-600 dark:text-gray-300'
            }`}>{task.description}</p>
          )}
          <div className="flex items-center justify-between">
            {showDate && task.due_date && (
              <span className={`text-xs ${
                theme === 'executive' 
                  ? 'text-[#0D2847] font-executive-body' 
                  : 'text-slate-500 dark:text-gray-400'
              }`}>
                {task.due_date}
              </span>
            )}
            <div className={showDate ? '' : 'ml-auto'}>
              {(task.status === 'in-process' || task.status === 'waiting') && (
                <span
                  className={`hidden print-show print-status print-status-${task.category.toLowerCase()} text-xs font-semibold italic`}
                >
                  {task.status === 'in-process' ? 'In-Process' : 'Waiting'}
                </span>
              )}
              <select
                value={task.status || 'none'}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  if (newStatus === 'complete' && !task.completed) playCheckSound();
                  else if (newStatus !== 'complete' && task.completed) playUncheckSound();
                  onStatusChange && onStatusChange(task.id, newStatus);
                }}
                className={`h-6 sm:h-7 w-[100px] sm:w-[110px] text-xs rounded-md border px-2 print-hide ${getStatusColor(task.status)} shadow-sm appearance-none bg-no-repeat bg-[length:12px] bg-[right_4px_center]`}
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")` }}
                data-testid="task-status-select"
              >
                <option value="none"></option>
                <option value="complete">Complete</option>
                <option value="in-process">In-Process</option>
                <option value="waiting">Waiting</option>
              </select>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default TaskCard;